import { ArrowLeftIcon, InfoCircleIcon } from '@langchain/untitled-ui-icons';

import { useNavigate } from 'react-router-dom';

import { Button, IconButton, Text, Tooltip } from '@/design-system';
import { cn } from '@/design-system/utils/cn';
import { useHostProject, useOrganizationId } from '@/hooks/useSwr';
import { appOrganizationPath, hostAppIndexPath } from '@/utils/constants';

import { useLangGraphClient } from '../../api';
import { useGraphServerInfo } from '../../api/useGraphSwr';
import { useLanggraphApiVersion } from '../../hooks/useLanggraphApiVersion';
import { isHostedUrl, isOutdatedLanggraphApi } from '../../utils';

export const LanggraphApiOutdatedAlert = () => {
  const [_, apiUrl] = useLangGraphClient();
  const { data: langgraphApiVersion, isLoading: isLoadingLanggraphApiVersion } =
    useLanggraphApiVersion();
  const { data: serverInfo, isLoading: isLoadingServerInfo } =
    useGraphServerInfo();
  const { version } = serverInfo ?? {};
  const { outdated, severity } = isOutdatedLanggraphApi({
    latestVersion: langgraphApiVersion,
    currentVersion: version,
  });

  const isHosted = isHostedUrl(apiUrl);

  if (!outdated || isLoadingLanggraphApiVersion || isLoadingServerInfo)
    return null;

  const ctaText = (() => {
    if (isHosted) {
      return ' creating a new revision.';
    }
    return (
      <Text variant="sm">
        {' '}
        running{' '}
        <Text
          as="span"
          className="font-mono text-secondary"
          variant="sm"
          weight="medium"
        >
          pip install -U langgraph-api
        </Text>
        .
      </Text>
    );
  })();

  return (
    <Tooltip
      title={
        <div className="flex flex-col gap-1">
          <Text>Current Server Version: {version}</Text>
          <Text>Latest Server Version: {langgraphApiVersion}</Text>

          <Text variant="sm" className="pt-2">
            Seeing issues? Try upgrading your server by{ctaText}
          </Text>
        </div>
      }
    >
      <div>
        <InfoCircleIcon
          className={cn('size-4 flex-shrink-0  cursor-pointer ', {
            'text-error': severity === 'critical',
            'text-warning': severity === 'medium',
          })}
        />
      </div>
    </Tooltip>
  );
};

export const BackButton = ({
  hostProjectId,
  compactHeader,
}: {
  hostProjectId?: string | null;
  compactHeader?: boolean;
}) => {
  const { data: project } = useHostProject(hostProjectId ?? null);
  const navigate = useNavigate();
  const organizationId = useOrganizationId();
  const onClick = () => {
    navigate(
      `/${appOrganizationPath}/${organizationId}/${hostAppIndexPath}/deployments${
        hostProjectId ? `/${hostProjectId}` : ''
      }`
    );
  };
  if (compactHeader) {
    return (
      <IconButton
        icon={ArrowLeftIcon}
        label="Back"
        onClick={onClick}
        color="secondary"
        variant="plain"
      />
    );
  }
  return (
    <div className="flex flex-shrink-0 items-center gap-2">
      <Button
        variant="plain"
        color="secondary"
        size="sm"
        onClick={onClick}
        leftDecorator={ArrowLeftIcon}
      >
        <Text
          variant="sm"
          weight="medium"
          className="whitespace-nowrap text-primary"
        >
          {hostProjectId ? project?.name : 'LangGraph Studio'}
        </Text>
      </Button>
    </div>
  );
};
